import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Building2, Wrench, Sun, Sparkles, ArrowRight, Quote, Users2, Landmark,
  Compass, ScrollText, ShieldCheck, Target,
} from 'lucide-react';
import { FOUNDER_CONTENT, FOUNDER_LABELS, pickLang } from '@/data/founderContent';
import { FOUNDER_PORTRAIT_URL } from '@/lib/config';

export default function FounderPage() {
  const { lang } = useLang();
  const L = pickLang(FOUNDER_LABELS, lang);
  const c = FOUNDER_CONTENT;

  return (
    <div>
      {/* HERO ------------------------------------------------------------- */}
      <section className="hero-surface">
        <Container className="py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <SectionKicker>{L.kicker}</SectionKicker>
              <h1
                className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220] tracking-[-0.01em]"
                data-testid="founder-name"
              >
                {c.name}
              </h1>
              <div className="mt-2 text-sm sm:text-base font-medium text-[#062B5C]">
                {L.hero_role}
              </div>
              <p className="mt-5 max-w-2xl text-[#5B6B82] text-base sm:text-lg leading-relaxed">
                {c.about[0]}
              </p>

              {/* Credentials chips */}
              <div className="mt-6 flex flex-wrap gap-2" data-testid="founder-credentials">
                <CredChip icon={Wrench} tone="#062B5C" label={L.tag_civil_eng} />
                <CredChip icon={Building2} tone="#F47B20" label={L.tag_entrepreneur} />
                <CredChip icon={Sun} tone="#138A4B" label={L.tag_infra} />
              </div>
            </div>

            {/* Portrait card */}
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative">
                <div
                  className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-[#F47B20]/10 via-white to-[#138A4B]/10 blur-2xl"
                  aria-hidden="true"
                />
                <div
                  className="relative rounded-3xl border border-[#D7E1EF] bg-white p-4 sm:p-5 shadow-lg"
                  data-testid="founder-portrait-card"
                >
                  <div className="overflow-hidden rounded-2xl bg-[#F5F8FC] aspect-[3/4] max-w-[320px] mx-auto">
                    <img
                      src={FOUNDER_PORTRAIT_URL}
                      alt={`${c.name}, Founder of NGRP`}
                      width={450}
                      height={600}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="w-full h-full object-cover"
                      data-testid="founder-hero-photo"
                    />
                  </div>
                  <div className="mt-5 text-center">
                    <div className="font-display text-xl font-semibold text-[#0B1220]">
                      {c.name}
                    </div>
                    <div className="mt-1 text-xs font-medium uppercase tracking-[0.14em] text-[#062B5C]">
                      Founder · NGRP
                    </div>
                    <div className="mt-3 text-[11px] text-[#5B6B82] leading-relaxed">
                      {c.short_line}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ABOUT THE FOUNDER ------------------------------------------------ */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionKicker>{L.about_kicker}</SectionKicker>
              <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1220] tracking-[-0.01em]">
                {L.about_title}
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5">
              <p className="text-[15px] sm:text-base text-[#0B1220] leading-relaxed">
                {c.about[1]}
              </p>
              <p className="text-[15px] sm:text-base text-[#5B6B82] leading-relaxed">
                {c.about[2]}
              </p>

              {/* Conviction quote */}
              <Card className="border-[#D7E1EF] bg-[#F5F8FC]" data-testid="founder-conviction">
                <CardContent className="p-5 sm:p-6 flex gap-4">
                  <div
                    className="h-9 w-9 shrink-0 rounded-lg bg-white border border-[#D7E1EF] flex items-center justify-center text-[#F47B20]"
                    aria-hidden="true"
                  >
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">
                      {L.conviction_label}
                    </div>
                    <p className="mt-2 font-display text-lg sm:text-xl text-[#0B1220] leading-snug">
                      “{c.conviction_quote}”
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </Container>
      </section>

      {/* WHY NGRP WAS FOUNDED --------------------------------------------- */}
      <section className="py-16">
        <Container>
          <SectionHeading
            eyebrow={L.why_kicker}
            title={L.why_title}
            subtitle={c.why_lede}
          />

          {/* Pipeline */}
          <div className="mt-8" data-testid="founder-pipeline">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">
              {L.pipeline_label}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
              {c.pipeline.map((step, idx) => (
                <React.Fragment key={step}>
                  <span
                    className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-[#062B5C]"
                    data-testid="founder-pipeline-step"
                  >
                    {step}
                  </span>
                  {idx < c.pipeline.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-[#5B6B82]" aria-hidden="true" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <InfoCard
              icon={Users2}
              tone="#F47B20"
              eyebrow={L.who_label}
              body={L.who_body}
              testid="founder-who-card"
            />
            <InfoCard
              icon={Landmark}
              tone="#138A4B"
              eyebrow={L.independence_label}
              body={L.independence_body}
              testid="founder-independence-card"
            />
          </div>
        </Container>
      </section>

      {/* GOVERNANCE PHILOSOPHY QUOTE -------------------------------------- */}
      <section className="py-16 bg-[#F5F8FC] border-y border-[#D7E1EF]">
        <Container>
          <SectionKicker>{L.philosophy_kicker}</SectionKicker>
          <h2 className="font-display mt-3 max-w-4xl text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1220] tracking-[-0.01em]">
            {L.philosophy_title}
          </h2>

          <Card className="mt-8 border-[#D7E1EF] bg-[#062B5C] text-white" data-testid="founder-philosophy-quote">
            <CardContent className="p-8 sm:p-10">
              <div
                className="h-10 w-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-[#F47B20]"
                aria-hidden="true"
              >
                <Quote className="h-5 w-5" />
              </div>
              <p className="mt-6 font-display text-xl sm:text-2xl lg:text-3xl leading-snug text-white">
                {c.philosophy_quote}
              </p>
              <div className="mt-6 text-sm text-white/70">
                — {c.name}, {L.hero_role}
              </div>
            </CardContent>
          </Card>

          <p className="mt-8 max-w-4xl text-[15px] sm:text-base text-[#0B1220] leading-relaxed">
            {L.philosophy_after}
          </p>
        </Container>
      </section>

      {/* NATIONAL MISSION ------------------------------------------------- */}
      <section className="py-16">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionKicker>{L.mission_kicker}</SectionKicker>
              <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1220] tracking-[-0.01em]">
                {L.mission_title}
              </h2>
              <p className="mt-4 text-[15px] sm:text-base text-[#5B6B82] leading-relaxed">
                {L.mission_body}
              </p>
            </div>
            <div className="lg:col-span-7">
              <Card className="border-[#D7E1EF] bg-white h-full" data-testid="founder-org-philosophy">
                <CardContent className="p-6 sm:p-8">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">
                    {L.org_philosophy_label}
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {c.org_philosophy.map((item, idx) => {
                      const icons = [Compass, ShieldCheck, Users2, Landmark];
                      const tones = ['#062B5C', '#F47B20', '#138A4B', '#062B5C'];
                      const Icon = icons[idx % icons.length];
                      const tone = tones[idx % tones.length];
                      return (
                        <div
                          key={item}
                          className="flex items-start gap-3 rounded-xl border border-[#D7E1EF] bg-[#F5F8FC] p-4"
                          data-testid="founder-org-philosophy-item"
                        >
                          <div
                            className="h-9 w-9 shrink-0 rounded-lg bg-white border border-[#D7E1EF] flex items-center justify-center"
                            style={{ color: tone }}
                            aria-hidden="true"
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="font-display text-sm sm:text-base font-semibold text-[#0B1220] leading-snug">
                            {item}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </Container>
      </section>

      {/* LONG-TERM OBJECTIVE ---------------------------------------------- */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-2 hidden lg:flex justify-center">
              <div
                className="h-14 w-14 rounded-2xl bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#F47B20]"
                aria-hidden="true"
              >
                <Target className="h-6 w-6" />
              </div>
            </div>
            <div className="lg:col-span-10">
              <SectionKicker>{L.objective_kicker}</SectionKicker>
              <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1220] tracking-[-0.01em]">
                {L.objective_title}
              </h2>
              <p
                className="mt-5 text-[15px] sm:text-lg text-[#0B1220] leading-relaxed max-w-4xl"
                data-testid="founder-long-term-objective"
              >
                {c.long_term_objective}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* SIGNATURE ---------------------------------------------------------- */}
      <section className="py-16 bg-[#F5F8FC] border-y border-[#D7E1EF]">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="text-center text-[10px] font-semibold uppercase tracking-[0.22em] text-[#062B5C]">
              {L.signature_label}
            </div>
            <Card
              className="mt-4 border-[#D7E1EF] bg-white shadow-sm"
              data-testid="founder-signature"
            >
              <CardContent className="p-8 sm:p-10 text-center">
                <div className="font-display text-3xl sm:text-4xl font-semibold text-[#0B1220] tracking-[-0.01em]">
                  {c.name}
                </div>
                <div className="mt-2 text-sm sm:text-base font-medium text-[#062B5C]">
                  {c.role}
                </div>
                <div className="mt-4 text-xs sm:text-sm text-[#5B6B82]">
                  {c.short_line}
                </div>

                <div className="mt-6 h-px w-16 mx-auto bg-[#D7E1EF]" aria-hidden="true" />

                <div className="mt-5 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#062B5C]">
                  {c.values_line}
                </div>
                <div className="mt-3 font-display text-sm sm:text-base font-semibold text-[#F47B20] tracking-[0.02em]">
                  {c.slogan}
                </div>
              </CardContent>
            </Card>

            {/* Cross-links */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link to="/vision">
                <Button
                  variant="outline"
                  className="w-full border-[#D7E1EF] text-[#062B5C] hover:bg-white"
                  data-testid="founder-cta-vision"
                >
                  <ScrollText className="h-4 w-4 mr-1.5" />
                  {L.read_vision}
                </Button>
              </Link>
              <Link to="/national">
                <Button
                  variant="outline"
                  className="w-full border-[#D7E1EF] text-[#062B5C] hover:bg-white"
                  data-testid="founder-cta-national"
                >
                  <Compass className="h-4 w-4 mr-1.5" />
                  {L.read_pan_india}
                </Button>
              </Link>
              <Link to="/constitution">
                <Button
                  variant="outline"
                  className="w-full border-[#D7E1EF] text-[#062B5C] hover:bg-white"
                  data-testid="founder-cta-constitution"
                >
                  <Landmark className="h-4 w-4 mr-1.5" />
                  {L.read_constitution}
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function CredChip({ icon: Icon, tone, label }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-[#0B1220]"
      data-testid="founder-cred-chip"
    >
      <Icon className="h-3.5 w-3.5" style={{ color: tone }} aria-hidden="true" />
      {label}
    </span>
  );
}

function InfoCard({ icon: Icon, tone, eyebrow, body, testid }) {
  return (
    <Card className="border-[#D7E1EF] bg-white h-full" data-testid={testid}>
      <CardContent className="p-6 h-full flex gap-4">
        <div
          className="h-10 w-10 shrink-0 rounded-xl bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center"
          style={{ color: tone }}
          aria-hidden="true"
        >
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: tone }}
          >
            {eyebrow}
          </div>
          <p className="mt-2 text-sm sm:text-base text-[#0B1220] leading-relaxed">
            {body}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
